import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { statusPageFooter } from './layout/footerConfigs'

type ServiceStatus = 'operational' | 'degraded' | 'outage'

interface Service {
  name: string
  status: ServiceStatus
  description?: string
  isBlurred?: boolean
}

interface ServiceCategory {
  category: string
  services: Service[]
}

interface Incident {
  date: string
  title: string
  duration: string
  status: 'resolved' | 'investigating'
}

export const StatusPage: React.FC = () => {
  const isMobile = useIsMobile()
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)

  const heroRef = React.useRef(null)
  const servicesRef = React.useRef(null)
  const historyRef = React.useRef(null)
  const isHeroInView = useInView(heroRef, { once: false, amount: 0.3 })
  const isServicesInView = useInView(servicesRef, { once: false, amount: 0.2 })
  const isHistoryInView = useInView(historyRef, { once: false, amount: 0.2 })

  // Detect prefers-reduced-motion
  const prefersReducedMotion = React.useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Overall system status
  const overallStatus: ServiceStatus = 'operational' // Can be 'operational', 'degraded', or 'outage'

  const getStatusColor = (status: ServiceStatus) => {
    switch (status) {
      case 'operational': return '#2ECC71'
      case 'degraded': return '#F39C12'
      case 'outage': return '#E74C3C'
    }
  }

  const getStatusText = (status: ServiceStatus) => {
    switch (status) {
      case 'operational': return 'All systems operational'
      case 'degraded': return 'Some services are experiencing issues'
      case 'outage': return 'Major outage detected'
    }
  }

  const getStatusIcon = (status: ServiceStatus) => {
    switch (status) {
      case 'operational': return '🟢'
      case 'degraded': return '🟡'
      case 'outage': return '🔴'
    }
  }

  const serviceCategories: ServiceCategory[] = [
    {
      category: 'Rare Earth Services',
      services: [
        { name: 'RareEarth Permit System', status: 'operational' },
        { name: 'RareEarth Buyer Matching', status: 'operational' }
      ]
    },
    {
      category: 'VerpackG / Compliance',
      services: [
        { name: 'VerpackG Löschung Backend', status: 'degraded', description: 'Experiencing slow response times' }
      ]
    },
    {
      category: 'Software Tools',
      services: [
        { name: 'Project Crimson Backend', status: 'operational', isBlurred: true },
        { name: 'Project Velocity Services', status: 'operational', isBlurred: true },
        { name: 'Project Nexus (Cloud Tools)', status: 'operational', isBlurred: true }
      ]
    },
    {
      category: 'Hardware Services',
      services: [
        { name: 'Project Quantum Device Services', status: 'operational', isBlurred: true },
        { name: 'Firmware Update Server', status: 'operational' }
      ]
    },
    {
      category: 'General Infrastructure',
      services: [
        { name: 'API Gateway', status: 'operational' },
        { name: 'Authentication', status: 'operational' },
        { name: 'Email Systems', status: 'degraded', description: 'Minor delays in email delivery' },
        { name: 'Database Cluster', status: 'operational' },
        { name: 'CDN / Cloudflare', status: 'operational' }
      ]
    }
  ]

  const incidents: Incident[] = [
    {
      date: 'Nov 12, 2026',
      title: 'VerpackG Löschung backend outage due to deploy misconfiguration',
      duration: '23 minutes',
      status: 'resolved'
    },
    {
      date: 'Nov 03, 2026',
      title: 'Email system delays',
      duration: '47 minutes',
      status: 'resolved'
    }
  ]

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      setSubscribed(true)
      setEmail('')
    }
  }

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper with Rounded Bottom Corners */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }
          }
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0],
              }
          }
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.15, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }
          }
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Hero Section */}
        <motion.section
          ref={heroRef}
          className="relative pt-48 pb-16"
          initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0 }}
          animate={isHeroInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight tracking-tight"
              style={{
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
              initial={prefersReducedMotion.current ? "visible" : "hidden"}
              animate={isHeroInView ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {'System Status'.split('').map((char, index) => (
                <motion.span
                  key={`char-${index}`}
                  variants={{
                    hidden: { opacity: 0, x: -20, y: 10 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: [0.4, 0.0, 0.2, 1]
                      }
                    }
                  }}
                  style={{ display: 'inline-block', whiteSpace: char === ' ' ? 'pre' : 'normal' }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.4, 0.0, 0.2, 1] }}
            >
              Real-time operational updates for Mernaki services, software, and hardware platforms.
            </motion.p>

            {/* Overall Status Banner */}
            <motion.div
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full"
              style={{
                backgroundColor: `${getStatusColor(overallStatus)}15`,
                border: `2px solid ${getStatusColor(overallStatus)}40`,
                boxShadow: `0 4px 16px ${getStatusColor(overallStatus)}20`
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 1.4, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <span style={{ fontSize: '24px' }}>{getStatusIcon(overallStatus)}</span>
              <span
                style={{
                  color: getStatusColor(overallStatus),
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '16px',
                  fontWeight: 600
                }}
              >
                {getStatusText(overallStatus)}
              </span>
            </motion.div>
          </div>
        </motion.section>

        {/* Services Status Section */}
        <section ref={servicesRef} className="relative pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {serviceCategories.map((category, catIndex) => (
                <motion.div
                  key={category.category}
                  className="p-6 rounded-2xl"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                    backdropFilter: 'blur(10px)'
                  }}
                  initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                  animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: catIndex * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                >
                  <h3
                    className="text-lg mb-4"
                    style={{
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 600
                    }}
                  >
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.services.map((service, serviceIndex) => (
                      <div
                        key={serviceIndex}
                        className="flex items-center justify-between p-3 rounded-lg transition-all duration-200"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span style={{ fontSize: '16px' }}>{getStatusIcon(service.status)}</span>
                            <span
                              style={{
                                color: '#1A1A1A',
                                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                fontSize: '15px',
                                fontWeight: 500,
                                filter: service.isBlurred ? 'blur(3px)' : 'none',
                                userSelect: service.isBlurred ? 'none' : 'auto',
                                pointerEvents: service.isBlurred ? 'none' : 'auto'
                              }}
                            >
                              {service.name}
                            </span>
                          </div>
                          {service.description && (
                            <p
                              className="mt-1 ml-7"
                              style={{
                                color: '#999999',
                                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                fontSize: '13px'
                              }}
                            >
                              {service.description}
                            </p>
                          )}
                        </div>
                        <span
                          style={{
                            color: getStatusColor(service.status),
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}
                        >
                          {service.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Incident History Section */}
        <section ref={historyRef} className="relative pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2
              className="text-3xl mb-8"
              style={{
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              animate={isHistoryInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
            >
              Incident History (Last 30 Days)
            </motion.h2>

            <div className="space-y-4">
              {incidents.map((incident, index) => (
                <motion.div
                  key={index}
                  className="p-5 rounded-xl"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    backdropFilter: 'blur(10px)'
                  }}
                  initial={prefersReducedMotion.current ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  animate={isHistoryInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span
                      style={{
                        color: '#666666',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {incident.date}
                    </span>
                    <span
                      className="px-3 py-1 rounded-full text-xs"
                      style={{
                        backgroundColor: '#2ECC7115',
                        color: '#2ECC71',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}
                    >
                      {incident.status}
                    </span>
                  </div>
                  <h4
                    className="mb-1"
                    style={{
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '15px',
                      fontWeight: 600
                    }}
                  >
                    {incident.title}
                  </h4>
                  <p
                    style={{
                      color: '#999999',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '13px'
                    }}
                  >
                    Duration: {incident.duration}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Scheduled Maintenance Section */}
        <section className="relative pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-3xl mb-8"
              style={{
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600
              }}
            >
              Scheduled Maintenance
            </h2>

            <div
              className="p-6 rounded-xl text-center"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <p
                style={{
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontSize: '15px'
                }}
              >
                No upcoming maintenance.
              </p>
            </div>
          </div>
        </section>

        {/* Subscribe Section */}
        <section className="relative pb-24">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="p-8 rounded-2xl text-center"
              style={{
                backgroundColor: 'rgba(74, 144, 226, 0.08)',
                border: '1px solid rgba(74, 144, 226, 0.2)',
                boxShadow: '0 4px 16px rgba(74, 144, 226, 0.1)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <h3
                className="text-2xl mb-3"
                style={{
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600
                }}
              >
                Subscribe for Status Updates
              </h3>
              <p
                className="text-base mb-6"
                style={{
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                }}
              >
                Get notified when service status changes
              </p>

              {!subscribed ? (
                <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-4 py-3 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '15px'
                    }}
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 rounded-lg transition-all duration-300"
                    style={{
                      backgroundColor: '#4A90E2',
                      color: '#FFFFFF',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '15px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(74, 144, 226, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#3A7BC8'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#4A90E2'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    Subscribe
                  </button>
                </form>
              ) : (
                <div
                  className="px-6 py-3 rounded-lg inline-block"
                  style={{
                    backgroundColor: 'rgba(46, 204, 113, 0.15)',
                    color: '#2ECC71',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '15px',
                    fontWeight: 600
                  }}
                >
                  ✓ Successfully subscribed!
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Footer - Conditional Mobile/Desktop */}
      {isMobile ? (
        <PageMobileFooter {...statusPageFooter} />
      ) : (
        <footer
          className="relative"
          style={{
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '140px',
            paddingBottom: '40px',
            marginTop: '-80px'
          }}
        >
          {/* Floating Gradient Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Main Footer Content - Compact Layout */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">

              {/* Left: Brand */}
              <div className="flex items-center gap-3">
                <img
                  src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                  alt="Mernaki Logo"
                  className="object-contain"
                  style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                  loading="lazy"
                />
                <div>
                  <h3
                    className="text-lg"
                    style={{
                      color: '#FFFFFF',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontWeight: 400,
                      marginBottom: '4px'
                    }}
                  >
                    Mernaki
                  </h3>
                  <p
                    className="text-xs"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 400
                    }}
                  >
                    Status
                  </p>
                </div>
              </div>

              {/* Center: Links */}
              <div className="flex items-center gap-6 flex-wrap justify-center">
                <Link
                  to="/"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Home
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/help"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Help Center
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/report-issue"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Report an Issue
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/privacy-policy"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Privacy Policy
                </Link>
                <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
                <Link
                  to="/impressum"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 400,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                >
                  Impressum
                </Link>
              </div>

              {/* Right: Contact */}
              <div className="text-center md:text-right">
                <a
                  href="mailto:support@mernaki.de"
                  className="text-sm transition-all duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                >
                  support@mernaki.de
                </a>
              </div>
            </div>

            {/* Bottom Row */}
            <div
              className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.3)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 300
                }}
              >
                Mernaki Status © {new Date().getFullYear()}
              </p>
              <p
                className="text-sm"
                style={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 400
                }}
              >
                Powered by Mernaki Monitoring.
              </p>
            </div>
          </div>
        </footer>
      )}
    </>
  )
}
